import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Stepper,
  Step,
  StepLabel,
  Tooltip,
  Slider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  DirectionsCar as CarIcon,
  Restaurant as RestaurantIcon,
  Hotel as HotelIcon,
  Attractions as AttractionsIcon,
  LocalGasStation as GasStationIcon,
  ExpandMore as ExpandMoreIcon,
  Navigation as NavigationIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Place as PlaceIcon,
  CheckCircle as CheckCircleIcon,
  CalendarMonth as CalendarIcon,
  Tune as TuneIcon,
  Route as RouteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import L from 'leaflet';
import polyline from '@mapbox/polyline';
import 'leaflet/dist/leaflet.css';
import api from '../config/api';
import LocationAutocomplete from './LocationAutocomplete';
import { loadGoogleMaps, isGoogleMapsLoaded } from '../utils/googleMapsLoader';
import { THEME_COLORS, THEME_GRADIENTS, THEME_SHADOWS } from '../constants/theme';
import { useAuth } from '../contexts/AuthContext';
import {
  addDaysToDateKey,
  clampDateKeyToTrip,
  formatRoadTripDayLabel,
  getDayIndexFromTripStart,
  isDateKeyWithinTrip,
  toDateKey,
} from '../utils/roadTripDateUtils';
import { getActiveCreatorTrips, parseTripDate } from '../utils/tripUtils';
import { findMatchingStop, findMatchingStopIndex, getStopsWithPlaces } from '../utils/roadTripStops';
import RoadTripStopsPanel from './RoadTripStopsPanel';

const STEPS = ['Plan Stops', 'View Route', 'Save Trip'];

// Laid out by the planner's own width (it also renders inside dialogs), so a
// container query is used instead of viewport breakpoints.
const ROUTE_STEP_WIDE = '@container (min-width: 880px)';

const routeStepGridSx = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  gridTemplateAreas: '"top" "panel" "details"',
  columnGap: 3,
  rowGap: 2,
  [ROUTE_STEP_WIDE]: {
    gridTemplateColumns: 'minmax(0, 1fr) 300px',
    gridTemplateAreas: '"top panel" "details panel"',
    gridTemplateRows: 'auto 1fr',
  },
};

// On full pages the panel has to clear the app's sticky header and tab bar.
const PAGE_STICKY_TOP_PX = 160;

const getRouteStepPanelSx = (inDialog) => {
  const stickyTop = inDialog ? 0 : PAGE_STICKY_TOP_PX;
  return {
    mb: 1,
    [ROUTE_STEP_WIDE]: {
      position: 'sticky',
      top: stickyTop,
      alignSelf: 'start',
      maxHeight: `calc(100vh - ${inDialog ? 160 : stickyTop + 24}px)`,
      overflowY: 'auto',
    },
  };
};
const PENDING_STATE_KEY = 'roadTripPlanner_pending';
// Uncached place lookups on long legs can outlast the default API timeout.
const POI_REQUEST_TIMEOUT_MS = 90000;

/**
 * RoadTripPlanner component.
 *
 * Props (all optional — when omitted it's "create" mode):
 *  - editMode: boolean — if true, the planner is pre-populated for editing
 *  - initialDays: array of day objects from allDaysData (road_trip_data)
 *  - initialTotalDays: number — total trip days (for edit mode, preserves skipped day slots)
 *  - editTripId: number — the trip to save back into
 *  - editGroupId: string — the roadTripGroupId to replace
 *  - onSaveComplete: () => void — called after a successful save in edit mode
 *  - compact: boolean — hides header / stepper labels when embedded in a dialog
 *  - itineraryMode: boolean — embed in trip itinerary; pick dates up front and save directly to targetTripId
 *  - targetTripId: number — trip to save into when itineraryMode is true
 *  - tripStartDate / tripEndDate: ISO date strings for the parent trip bounds
 *  - initialAnchorDate: Date|string — first road-trip day when launched from an itinerary day
 *  - initialActiveStep: number — open directly on a planner step (e.g. 1 = View Route / POIs)
 *  - usePartnerAuth: boolean — a signed-in partner is planning for a client trip they manage
 */
const RoadTripPlanner = ({
  editMode = false,
  initialDays,
  initialTotalDays,
  editTripId,
  editGroupId,
  onSaveComplete,
  compact = false,
  itineraryMode = false,
  targetTripId = null,
  tripStartDate = null,
  tripEndDate = null,
  initialAnchorDate = null,
  initialActiveStep = 0,
  usePartnerAuth = false,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isSignedIn = Boolean(user) || usePartnerAuth;
  const authConfig = useMemo(
    () => (usePartnerAuth ? { usePartnerAuth: true } : {}),
    [usePartnerAuth]
  );
  const navigate = useNavigate();
  const location = useLocation();
  const tripStartKey = toDateKey(tripStartDate);
  const tripEndKey = toDateKey(tripEndDate);
  const datePickMode = itineraryMode || (editMode && Boolean(tripStartKey && tripEndKey));

  const buildDayMeta = useCallback((dayIndex, dateKey) => {
    const resolvedDate = clampDateKeyToTrip(
      dateKey || addDaysToDateKey(tripStartKey || toDateKey(new Date()), dayIndex),
      tripStartKey,
      tripEndKey
    );
    const resolvedIndex = datePickMode && tripStartKey && resolvedDate
      ? getDayIndexFromTripStart(tripStartKey, resolvedDate)
      : dayIndex;

    return {
      dayIndex: resolvedIndex,
      date: datePickMode ? resolvedDate : undefined,
      label: datePickMode && resolvedDate
        ? formatRoadTripDayLabel(resolvedDate)
        : `${t('roadTrip.day')} ${dayIndex + 1}`,
    };
  }, [datePickMode, t, tripEndKey, tripStartKey]);

  // Step state — start at step 0 (plan stops) in both create and edit mode
  const [activeStep, setActiveStep] = useState(initialActiveStep);
  const routesHydratedRef = useRef(false);
  const [signInDialogOpen, setSignInDialogOpen] = useState(false);

  // Build initial days from editMode data or default (preserves skipped day slots via dayIndex)
  const buildInitialDays = useCallback(() => {
    if (editMode && initialDays && initialDays.length > 0) {
      let stopIdCounter = 1;
      const totalSlots = initialTotalDays != null && initialTotalDays > 0
        ? initialTotalDays
        : (initialDays.some(d => d.dayIndex != null)
            ? Math.max(...initialDays.map(d => d.dayIndex ?? 0), 0) + 1
            : initialDays.length);
      const result = [];
      for (let dayIndex = 0; dayIndex < totalSlots; dayIndex++) {
        const d = initialDays.find(x => x.dayIndex === dayIndex) ?? (dayIndex < initialDays.length ? initialDays[dayIndex] : null);
        const dayStops = d?.stops || [];
        const startId = stopIdCounter;
        stopIdCounter += dayStops.length;
        const stops = dayStops.map((s, idx) => ({
          id: startId + idx,
          name: s.name || '',
          address: s.address || s.name || '',
          lat: s.lat,
          lng: s.lng,
          placeId: null,
          savedPois: (s.savedPois || []).map(p => ({
            ...p,
            types: p.category === 'restaurant'
              ? ['restaurant']
              : p.category === 'hotel'
                ? ['lodging']
                : p.category === 'gas_station'
                  ? ['gas_station']
                  : ['tourist_attraction']
          }))
        }));
        // Empty/skipped days get 2 placeholder stops for consistency
        const placeholderId1 = stopIdCounter++;
        const placeholderId2 = stopIdCounter++;
        const finalStops = stops.length >= 2 ? stops : [
          { id: placeholderId1, name: '', address: '', lat: null, lng: null, placeId: null, savedPois: [] },
          { id: placeholderId2, name: '', address: '', lat: null, lng: null, placeId: null, savedPois: [] }
        ];
        const dayMeta = buildDayMeta(
          dayIndex,
          d?.date || (tripStartKey ? addDaysToDateKey(tripStartKey, dayIndex) : null)
        );
        result.push({
          id: dayIndex + 1,
          dayIndex: dayMeta.dayIndex,
          date: dayMeta.date,
          label: dayMeta.label,
          stops: finalStops
        });
      }
      return result;
    }
    if (itineraryMode) {
      const anchorKey = clampDateKeyToTrip(
        toDateKey(initialAnchorDate) || tripStartKey,
        tripStartKey,
        tripEndKey
      );
      const dayMeta = buildDayMeta(0, anchorKey);
      return [{
        id: 1,
        dayIndex: dayMeta.dayIndex,
        date: dayMeta.date,
        label: dayMeta.label,
        stops: [
          { id: 1, name: '', address: '', lat: null, lng: null, placeId: null, savedPois: [] },
          { id: 2, name: '', address: '', lat: null, lng: null, placeId: null, savedPois: [] }
        ]
      }];
    }
    return [{ id: 1, dayIndex: 0, label: `${t('roadTrip.day')} 1`, stops: [
      { id: 1, name: '', address: '', lat: null, lng: null, placeId: null, savedPois: [] },
      { id: 2, name: '', address: '', lat: null, lng: null, placeId: null, savedPois: [] }
    ]}];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, initialDays, initialTotalDays, itineraryMode, initialAnchorDate, tripStartKey, tripEndKey, buildDayMeta]);

  // Multi-day state
  const [days, setDays] = useState(buildInitialDays);
  // activeDayTab removed — using stacked day cards instead of tabs
  const [nextDayId, setNextDayId] = useState(() => {
    if (editMode && initialDays && initialTotalDays != null) return initialTotalDays + 1;
    if (editMode && initialDays) return initialDays.length + 1;
    return 2;
  });
  const [nextStopId, setNextStopId] = useState(() => {
    if (editMode && initialDays) {
      const totalSlots = initialTotalDays != null && initialTotalDays > 0
        ? initialTotalDays
        : (initialDays.some(d => d.dayIndex != null)
            ? Math.max(...initialDays.map(d => d.dayIndex ?? 0), 0) + 1
            : initialDays.length);
      let count = 0;
      for (let i = 0; i < totalSlots; i++) {
        const d = initialDays.find(x => x.dayIndex === i) ?? (i < initialDays.length ? initialDays[i] : null);
        count += (d?.stops?.length || 0) >= 2 ? (d?.stops?.length || 0) : 2;
      }
      return count + 1;
    }
    return 3;
  });

  const buildRouteFromDay = useCallback((day, dayIdx) => {
    const validStops = (day.stops || []).filter(
      (stop) => stop.lat != null && stop.lng != null && typeof stop.lat === 'number'
    );
    if (validStops.length < 2) {
      return null;
    }

    const sourceDay = initialDays?.find((entry) => entry.dayIndex === day.dayIndex) ?? initialDays?.[dayIdx];
    const orderedStops = validStops.map((stop) => ({
      name: stop.name || stop.address,
      lat: stop.lat,
      lng: stop.lng,
      address: stop.address || stop.name,
    }));

    let legs = sourceDay?.legs || [];
    if (!legs.length) {
      legs = orderedStops.slice(1).map((stop, index) => ({
        startLocation: { lat: orderedStops[index].lat, lng: orderedStops[index].lng },
        endLocation: { lat: stop.lat, lng: stop.lng },
      }));
    }

    return {
      orderedStops,
      legs,
      encodedPolyline: sourceDay?.encodedPolyline || null,
      totalDistance: sourceDay?.totalDistance || null,
      totalDuration: sourceDay?.totalDuration || null,
      googleMapsUrl: sourceDay?.googleMapsUrl || null,
      stopCount: orderedStops.length,
    };
  }, [initialDays]);

  // Restore planner state after login (only in create mode, not edit)
  useEffect(() => {
    if (editMode) return;
    try {
      const raw = sessionStorage.getItem(PENDING_STATE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      sessionStorage.removeItem(PENDING_STATE_KEY);
      if (saved?.days && Array.isArray(saved.days) && saved.days.length > 0) {
        setDays(saved.days);
        if (saved.nextDayId != null) setNextDayId(saved.nextDayId);
        if (saved.nextStopId != null) setNextStopId(saved.nextStopId);
      }
    } catch (e) {
      sessionStorage.removeItem(PENDING_STATE_KEY);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode]);

  // Helper to update stops for a specific day
  const setDayStops = (dayIdx, updater) => {
    setDays(prev => prev.map((day, idx) => 
      idx === dayIdx 
        ? { ...day, stops: typeof updater === 'function' ? updater(day.stops) : updater }
        : day
    ));
  };
  
  // Route state (per-day: { dayId -> routeData })
  const [allRoutesData, setAllRoutesData] = useState({});
  const [allPoisData, setAllPoisData] = useState({});
  const [routeLoading, setRouteLoading] = useState(false);
  // dayIdx -> 'loading' | 'failed'; days fetch independently, so one
  // finishing must not end another day's spinner.
  const [poisStatusByDay, setPoisStatusByDay] = useState({});
  const [error, setError] = useState(null);
  const [activeRouteDay, setActiveRouteDay] = useState(0); // which day tab is shown in step 2

  // Current day's route and POI data
  const routeData = allRoutesData[activeRouteDay] || null;
  const poisData = allPoisData[activeRouteDay] || null;

  // At least one day has a route (empty days are skipped)
  const hasAnyRoute = days.some((_, idx) => allRoutesData[idx]);
  
  // Map state
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const poiMarkersRef = useRef([]);
  const polylineRef = useRef(null);
  
  // Save dialog state
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveMode, setSaveMode] = useState('new'); // 'new' or 'existing'
  const [newTripTitle, setNewTripTitle] = useState('');
  const [newTripStartDate, setNewTripStartDate] = useState('');
  const [newTripEndDate, setNewTripEndDate] = useState('');
  const [newTripDescription, setNewTripDescription] = useState('');
  const [existingTrips, setExistingTrips] = useState([]);
  const [selectedTripId, setSelectedTripId] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  // Each save writes a new road trip group, so a double click would duplicate the whole trip.
  const saveInFlightRef = useRef(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [savedTripId, setSavedTripId] = useState(null);

  // Optimizer: split route by max driving hours
  const [optimizerDialogOpen, setOptimizerDialogOpen] = useState(false);
  const [maxDrivingHours, setMaxDrivingHours] = useState(8);
  const [optimizeLoading, setOptimizeLoading] = useState(false);

  // Map breadcrumb: show full route or current day only
  const [showFullRoute, setShowFullRoute] = useState(false);
  const [hasUnsavedPlaces, setHasUnsavedPlaces] = useState(false);

  const saveButtonLabel = saveLoading
    ? t('common.loading')
    : editMode
      ? t('roadTrip.updateTrip')
      : itineraryMode
        ? t('roadTrip.saveToItinerary')
        : t('roadTrip.saveTrip');


  // ============ STEP 1: PLAN STOPS ============

  const handleAddDay = () => {
    if (datePickMode) {
      const lastDay = days[days.length - 1];
      const nextDate = addDaysToDateKey(lastDay.date || tripStartKey, 1);
      if (!isDateKeyWithinTrip(nextDate, tripStartKey, tripEndKey)) {
        setError(t('roadTrip.dateOutOfRange'));
        return;
      }
      const dayMeta = buildDayMeta(days.length, nextDate);
      setDays(prev => [...prev, {
        id: nextDayId,
        dayIndex: dayMeta.dayIndex,
        date: dayMeta.date,
        label: dayMeta.label,
        stops: [
          { id: nextStopId, name: '', address: '', lat: null, lng: null, placeId: null, savedPois: [] },
          { id: nextStopId + 1, name: '', address: '', lat: null, lng: null, placeId: null, savedPois: [] }
        ]
      }]);
      setNextDayId(prev => prev + 1);
      setNextStopId(prev => prev + 2);
      return;
    }

    const newDayNum = days.length + 1;
    setDays(prev => [...prev, {
      id: nextDayId,
      dayIndex: prev.length,
      label: `${t('roadTrip.day')} ${newDayNum}`,
      stops: [
        { id: nextStopId, name: '', address: '', lat: null, lng: null, placeId: null, savedPois: [] },
        { id: nextStopId + 1, name: '', address: '', lat: null, lng: null, placeId: null, savedPois: [] }
      ]
    }]);
    setNextDayId(prev => prev + 1);
    setNextStopId(prev => prev + 2);
  };

  const handleDayDateChange = (dayIdx, nextDateKey) => {
    if (!datePickMode || !nextDateKey) return;
    const clampedDate = clampDateKeyToTrip(nextDateKey, tripStartKey, tripEndKey);
    if (!isDateKeyWithinTrip(clampedDate, tripStartKey, tripEndKey)) {
      setError(t('roadTrip.dateOutOfRange'));
      return;
    }
    setError(null);
    setDays(prev => prev.map((day, index) => {
      if (index !== dayIdx) return day;
      const dayMeta = buildDayMeta(index, clampedDate);
      return {
        ...day,
        dayIndex: dayMeta.dayIndex,
        date: dayMeta.date,
        label: dayMeta.label,
      };
    }));
  };

  const handleRemoveDay = (dayIdx) => {
    if (days.length <= 1) return;
    setDays(prev => {
      const updated = prev.filter((_, idx) => idx !== dayIdx);
      if (datePickMode) {
        return updated.map((day, index) => {
          const dayMeta = buildDayMeta(index, day.date);
          return { ...day, dayIndex: dayMeta.dayIndex, date: dayMeta.date, label: dayMeta.label };
        });
      }
      // Re-label days and update dayIndex to match array position
      return updated.map((d, i) => ({ ...d, dayIndex: i, label: `${t('roadTrip.day')} ${i + 1}` }));
    });
    // Also remove route/POI data for this day and reindex
    setAllRoutesData(prev => {
      const newData = {};
      Object.keys(prev).forEach(k => {
        const ki = parseInt(k);
        if (ki < dayIdx) { newData[ki] = prev[ki]; }
        else if (ki > dayIdx) { newData[ki - 1] = prev[ki]; }
      });
      return newData;
    });
    setAllPoisData(prev => {
      const newData = {};
      Object.keys(prev).forEach(k => {
        const ki = parseInt(k);
        if (ki < dayIdx) { newData[ki] = prev[ki]; }
        else if (ki > dayIdx) { newData[ki - 1] = prev[ki]; }
      });
      return newData;
    });
    // No tab to adjust — stacked cards show all days
  };

  const handleAddStop = (dayIdx) => {
    setDayStops(dayIdx, prev => [...prev, { id: nextStopId, name: '', address: '', lat: null, lng: null, placeId: null, savedPois: [] }]);
    setNextStopId(prev => prev + 1);
  };

  const handleRoundTrip = (dayIdx) => {
    const dayStops = days[dayIdx]?.stops || [];
    const first = dayStops[0];
    if (!first || dayStops.length < 2) return;
    if (!first.name && !first.address) return;
    setDayStops(dayIdx, prev => [...prev, {
      id: nextStopId,
      name: first.name || '',
      address: first.address || first.name || '',
      lat: first.lat,
      lng: first.lng,
      placeId: first.placeId,
      savedPois: first.savedPois || [],
    }]);
    setNextStopId(prev => prev + 1);
  };

  const handleRemoveStop = (dayIdx, id) => {
    const dayStops = days[dayIdx]?.stops || [];
    if (dayStops.length <= 2) return; // Minimum 2 stops
    setDayStops(dayIdx, prev => prev.filter(s => s.id !== id));
  };

  const handleStopChange = (dayIdx, id, value) => {
    setDayStops(dayIdx, prev => prev.map(s => s.id === id ? { ...s, address: value } : s));
  };

  const handlePlaceSelect = (dayIdx, id, placeData) => {
    setDayStops(dayIdx, prev => prev.map(s => 
      s.id === id 
        ? { 
            ...s, 
            name: placeData.name, 
            address: placeData.address, 
            lat: placeData.coordinates?.lat || null, 
            lng: placeData.coordinates?.lng || null,
            placeId: placeData.placeId 
          } 
        : s
    ));
  };

  const handleMoveStop = (dayIdx, index, direction) => {
    const dayStops = [...(days[dayIdx]?.stops || [])];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= dayStops.length) return;
    [dayStops[index], dayStops[newIndex]] = [dayStops[newIndex], dayStops[index]];
    setDayStops(dayIdx, dayStops);
  };

  // Can plan when at least one day has 2+ stops (empty days are skipped)
  const canPlanRoute = days.some(day => {
    const validStops = day.stops.filter(s => {
      const hasCoords = s.lat != null && s.lng != null && typeof s.lat === 'number' && typeof s.lng === 'number';
      const hasAddress = s.address && String(s.address).trim().length >= 5;
      return hasCoords || hasAddress;
    });
    return validStops.length >= 2;
  });

  // Geocode an address to get lat/lng
  const geocodeAddress = async (address) => {
    if (!isGoogleMapsLoaded()) await loadGoogleMaps();
    const google = window.google;
    if (!google?.maps?.Geocoder) return null;
    return new Promise((resolve) => {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: address.trim() }, (results, status) => {
        if (status === 'OK' && results?.[0]?.geometry?.location) {
          const loc = results[0].geometry.location;
          resolve({ lat: loc.lat(), lng: loc.lng() });
        } else {
          resolve(null);
        }
      });
    });
  };

  // ============ STEP 2: PLAN ROUTE ============

  const handlePlanRoute = async () => {
    if (!canPlanRoute) return;

    // Require sign-in before planning (only in create mode; edit mode is always authenticated)
    if (!editMode && !isSignedIn) {
      setSignInDialogOpen(true);
      return;
    }

    setRouteLoading(true);
    setError(null);
    setAllPoisData({});
    setAllRoutesData({});

    try {
      // Geocode any stops that have address but no coordinates
      const daysWithCoords = await Promise.all(days.map(async (day, dayIdx) => {
        const stops = await Promise.all(day.stops.map(async (s) => {
          const hasCoords = s.lat != null && s.lng != null && typeof s.lat === 'number' && typeof s.lng === 'number';
          if (hasCoords) return s;
          const hasAddress = s.address && String(s.address).trim().length >= 5;
          if (!hasAddress) return s;
          const coords = await geocodeAddress(s.address);
          if (coords) {
            return { ...s, lat: coords.lat, lng: coords.lng, name: s.name || s.address };
          }
          return s;
        }));
        return { ...day, stops };
      }));

      // Update state with geocoded coordinates
      setDays(daysWithCoords);

      const newRoutes = {};
      let firstRouteDayIdx = null;
      // Optimize each day's route (skip days with < 2 stops)
      for (let dayIdx = 0; dayIdx < daysWithCoords.length; dayIdx++) {
        const dayStops = daysWithCoords[dayIdx].stops.filter(s => s.lat != null && s.lng != null && typeof s.lat === 'number' && typeof s.lng === 'number');
        if (dayStops.length < 2) {
          continue; // Skip this day
        }
        const stopsPayload = dayStops.map(s => ({ name: s.name || s.address, lat: s.lat, lng: s.lng }));
        const response = await api.post('/road-trip/optimize', { stops: stopsPayload, preserveOrder: true }, authConfig);
        
        if (response.data.success) {
          newRoutes[dayIdx] = response.data;
          if (firstRouteDayIdx === null) firstRouteDayIdx = dayIdx;
        } else {
          setError(`${daysWithCoords[dayIdx].label}: ${response.data.error || t('roadTrip.routeError')}`);
          setRouteLoading(false);
          return;
        }
      }

      if (Object.keys(newRoutes).length === 0) {
        setError(t('roadTrip.allDaysSkipped'));
        setRouteLoading(false);
        return;
      }

      setAllRoutesData(newRoutes);
      setActiveRouteDay(firstRouteDayIdx);
      setActiveStep(1);
      
      // Auto-generate trip title from first and last stops across planned days (no "Planner" prefix)
      const routeDayIndices = Object.keys(newRoutes).map(Number).sort((a, b) => a - b);
      const firstDayRoute = newRoutes[routeDayIndices[0]];
      const lastDayRoute = newRoutes[routeDayIndices[routeDayIndices.length - 1]];
      const firstStop = firstDayRoute?.orderedStops?.[0]?.name || '';
      const lastStop = lastDayRoute?.orderedStops?.[lastDayRoute.orderedStops.length - 1]?.name || '';
      const daysSuffix = routeDayIndices.length > 1 ? ` (${routeDayIndices.length} ${t('roadTrip.days')})` : '';
      if (firstStop && lastStop && firstStop === lastStop) {
        setNewTripTitle(`${t('roadTrip.roundTripFrom', { city: firstStop })}${daysSuffix}`);
      } else if (firstStop || lastStop) {
        setNewTripTitle(`${firstStop} ${t('roadTrip.to')} ${lastStop}${daysSuffix}`);
      } else {
        setNewTripTitle(t('roadTrip.title'));
      }

      fetchPOIsForDays(newRoutes, routeDayIndices);
    } catch (err) {
      console.error('Route optimization error:', err);
      setError(err.response?.data?.error || t('roadTrip.routeError'));
    } finally {
      setRouteLoading(false);
    }
  };

  // Flatten all stops across days (dedupe overnight stops) for optimize-and-split
  const getFlattenedStopsForOptimizer = useCallback(() => {
    const routeDayIndices = Object.keys(allRoutesData).map(Number).sort((a, b) => a - b);
    if (routeDayIndices.length === 0) return [];
    let allStops = [];
    for (const idx of routeDayIndices) {
      const route = allRoutesData[idx];
      if (!route?.orderedStops?.length) continue;
      const dayStops = route.orderedStops;
      if (allStops.length === 0) {
        allStops = [...dayStops];
      } else {
        allStops.push(...dayStops.slice(1));
      }
    }
    return allStops;
  }, [allRoutesData]);

  const handleOptimizeDays = async () => {
    const stops = getFlattenedStopsForOptimizer();
    if (stops.length < 2) return;
    setOptimizeLoading(true);
    setError(null);
    try {
      const response = await api.post('/road-trip/optimize-and-split', {
        stops: stops.map(s => ({ name: s.name, lat: s.lat, lng: s.lng })),
        maxDrivingHoursPerDay: maxDrivingHours,
      }, authConfig);
      if (!response.data.success || !response.data.days?.length) {
        setError(t('roadTrip.routeError'));
        return;
      }
      const apiDays = response.data.days;
      let stopIdCounter = 1;
      const newDays = apiDays.map((d, dayIdx) => ({
        id: dayIdx + 1,
        dayIndex: dayIdx,
        label: `${t('roadTrip.day')} ${dayIdx + 1}`,
        stops: (d.stops || d.orderedStops || []).map((s, i) => ({
          id: stopIdCounter++,
          name: s.name || '',
          address: s.address || s.name || '',
          lat: s.lat,
          lng: s.lng,
          placeId: null,
          savedPois: s.savedPois || [],
        })),
      }));
      const newRoutes = {};
      apiDays.forEach((d, idx) => {
        const orderedStops = d.orderedStops || d.stops || [];
        newRoutes[idx] = {
          orderedStops,
          legs: d.legs || [],
          encodedPolyline: d.encodedPolyline,
          totalDistance: d.totalDistance,
          totalDuration: d.totalDuration,
          googleMapsUrl: d.googleMapsUrl,
          stopCount: orderedStops.length,
        };
      });
      setDays(newDays);
      setAllRoutesData(newRoutes);
      setAllPoisData({});
      setActiveRouteDay(0);
      setOptimizerDialogOpen(false);
      setNextDayId(newDays.length + 1);
      setNextStopId(stopIdCounter);
      fetchPOIsForDays(newRoutes, apiDays.map((_, i) => i));
    } catch (err) {
      console.error('Optimize days error:', err);
      setError(err.response?.data?.error || t('roadTrip.routeError'));
    } finally {
      setOptimizeLoading(false);
    }
  };

  const setPoisStatus = (dayIdx, status) => {
    setPoisStatusByDay(prev => {
      const next = { ...prev };
      if (status) next[dayIdx] = status;
      else delete next[dayIdx];
      return next;
    });
  };

  const fetchPOIsForDay = async (route, dayIdx) => {
    setPoisStatus(dayIdx, 'loading');
    try {
      const legsPayload = route.legs.map(leg => ({
        startLat: leg.startLocation.lat,
        startLng: leg.startLocation.lng,
        endLat: leg.endLocation.lat,
        endLng: leg.endLocation.lng
      }));
      const response = await api.post('/road-trip/pois', {
        legs: legsPayload,
        stops: route.orderedStops,
        encodedPolyline: route.encodedPolyline || undefined,
        tripId: editTripId || targetTripId || undefined
      }, { ...authConfig, timeout: POI_REQUEST_TIMEOUT_MS });
      if (!response.data?.success || !Array.isArray(response.data.poisByLeg)) {
        throw new Error('Invalid POI response');
      }
      setAllPoisData(prev => ({ ...prev, [dayIdx]: response.data.poisByLeg }));
      setPoisStatus(dayIdx, null);
    } catch (err) {
      console.error('POI fetch error:', err);
      setPoisStatus(dayIdx, 'failed');
    }
  };

  // One day at a time: the backend shares a small Overpass quota across users.
  const fetchPOIsForDays = async (routesByDay, dayIndices) => {
    setPoisStatusByDay(prev => ({
      ...prev,
      ...Object.fromEntries(dayIndices.map(dayIdx => [dayIdx, 'loading'])),
    }));
    for (const dayIdx of dayIndices) {
      await fetchPOIsForDay(routesByDay[dayIdx], dayIdx);
    }
  };

  const renderNoPois = (unavailable) => (
    <Box data-testid="road-trip-no-pois" sx={{ py: 2, textAlign: 'center' }}>
      <Typography variant="body2" color="text.secondary">
        {unavailable ? t('roadTrip.poisUnavailable') : t('roadTrip.noPOIs')}
      </Typography>
      {unavailable && (
        <Button size="small" sx={{ mt: 1 }} onClick={() => fetchPOIsForDay(routeData, activeRouteDay)}>
          {t('roadTrip.retryPOIs')}
        </Button>
      )}
    </Box>
  );

  useEffect(() => {
    if (!editMode || routesHydratedRef.current || !days.length) {
      return;
    }

    const routes = {};
    days.forEach((day, dayIdx) => {
      const route = buildRouteFromDay(day, dayIdx);
      if (route) {
        routes[dayIdx] = route;
      }
    });

    const routeDayIndices = Object.keys(routes).map(Number);
    if (!routeDayIndices.length) {
      return;
    }

    routesHydratedRef.current = true;
    setAllRoutesData(routes);
    setActiveRouteDay(Math.min(...routeDayIndices));
    fetchPOIsForDays(routes, routeDayIndices);

    if (initialActiveStep === 1) {
      setActiveStep(1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, days, buildRouteFromDay, initialActiveStep]);

  const handleAddPOIToRoute = (poi, stopIndex, dayIdx) => {
    const dayRoute = allRoutesData[dayIdx];
    if (!dayRoute) return;
    const orderedStop = dayRoute.orderedStops[stopIndex];
    if (!orderedStop) return;

    const dayStops = days[dayIdx]?.stops || [];
    const matchIdx = findMatchingStopIndex(dayStops, orderedStop, stopIndex);
    if (matchIdx === -1) return;

    // Toggle: if already added, remove it; if not, add it
    const alreadyAdded = dayStops[matchIdx].savedPois?.some(p => p.placeId === poi.placeId);

    setDays(prev => prev.map((day, di) => {
      if (di !== dayIdx) return day;
      return {
        ...day,
        stops: day.stops.map((s, si) => {
          if (si !== matchIdx) return s;
          if (alreadyAdded) {
            return { ...s, savedPois: (s.savedPois || []).filter(p => p.placeId !== poi.placeId) };
          } else {
            return { ...s, savedPois: [...(s.savedPois || []), poi] };
          }
        })
      };
    }));
    setHasUnsavedPlaces(true);
  };

  const handleRemovePlace = (dayIdx, stopId, placeId) => {
    setDays(prev => prev.map((day, di) => (di !== dayIdx ? day : {
      ...day,
      stops: day.stops.map(s => (s.id !== stopId ? s : {
        ...s,
        savedPois: (s.savedPois || []).filter(p => p.placeId !== placeId)
      }))
    })));
    setHasUnsavedPlaces(true);
  };

  // Get the Set of saved placeIds for a given orderedStop index on a specific day
  const getSavedPlaceIds = (orderedStopIndex, dayIdx) => {
    const dayRoute = allRoutesData[dayIdx];
    if (!dayRoute) return new Set();
    const orderedStop = dayRoute.orderedStops?.[orderedStopIndex];
    if (!orderedStop) return new Set();
    const matchedStop = findMatchingStop(days[dayIdx]?.stops || [], orderedStop, orderedStopIndex);
    return new Set((matchedStop?.savedPois || []).map(p => p.placeId));
  };

  // ============ MAP RENDERING (Leaflet + OpenStreetMap) ============

  const renderMap = useCallback(() => {
    const firstRoute = routeData || Object.values(allRoutesData)[0];
    if (!firstRoute || !mapRef.current) return;

    try {
      // Clear previous map elements
      markersRef.current.forEach(m => {
        if (mapInstanceRef.current) mapInstanceRef.current.removeLayer(m);
      });
      markersRef.current = [];
      if (polylineRef.current && mapInstanceRef.current) {
        mapInstanceRef.current.removeLayer(polylineRef.current);
        polylineRef.current = null;
      }

      const firstStop = firstRoute.orderedStops[0];
      if (!firstStop) return;

      // Initialize Leaflet map if not already created
      if (!mapInstanceRef.current) {
        mapInstanceRef.current = L.map(mapRef.current, {
          zoomControl: true,
          scrollWheelZoom: true,
        }).setView([firstStop.lat, firstStop.lng], 6);

        // OpenStreetMap tile layer (free, no API key)
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxZoom: 19
        }).addTo(mapInstanceRef.current);
      }

      const map = mapInstanceRef.current;

      // Decode and render polyline(s) — full route or single day
      if (showFullRoute && Object.keys(allRoutesData).length > 1) {
        const layerGroup = L.layerGroup();
        Object.keys(allRoutesData)
          .map(Number)
          .sort((a, b) => a - b)
          .forEach((idx) => {
            const r = allRoutesData[idx];
            if (!r?.encodedPolyline) return;
            const decoded = polyline.decode(r.encodedPolyline);
            L.polyline(decoded, { color: THEME_COLORS.primary.main, weight: 4, opacity: 0.7 }).addTo(layerGroup);
          });
        layerGroup.addTo(map);
        polylineRef.current = layerGroup;
      } else if (routeData?.encodedPolyline) {
        const decodedPath = polyline.decode(routeData.encodedPolyline);
        polylineRef.current = L.polyline(decodedPath, {
          color: THEME_COLORS.primary.main,
          weight: 5,
          opacity: 0.85,
          smoothFactor: 1
        }).addTo(map);
      }

      // Build stops list (full route or single day)
      let stopsToMark = [];
      if (showFullRoute && Object.keys(allRoutesData).length > 1) {
        const indices = Object.keys(allRoutesData).map(Number).sort((a, b) => a - b);
        indices.forEach((idx) => {
          const r = allRoutesData[idx];
          if (!r?.orderedStops) return;
          if (stopsToMark.length === 0) stopsToMark = [...r.orderedStops];
          else stopsToMark.push(...r.orderedStops.slice(1));
        });
      } else {
        stopsToMark = routeData?.orderedStops || [];
      }

      // Add numbered circle markers for each stop
      const bounds = L.latLngBounds([]);
      const first = stopsToMark[0];
      const last = stopsToMark[stopsToMark.length - 1];
      const isRoundTrip = stopsToMark.length >= 2 && first && last &&
        Math.abs((first.lat || 0) - (last.lat || 0)) < 1e-6 &&
        Math.abs((first.lng || 0) - (last.lng || 0)) < 1e-6;
      // For round trips, draw in reverse order so start (1) is on top of end (n) at same location
      const drawOrder = isRoundTrip ? [...stopsToMark].reverse() : stopsToMark;

      drawOrder.forEach((stop, i) => {
        const index = isRoundTrip ? stopsToMark.length - 1 - i : i;
        const latlng = [stop.lat, stop.lng];
        bounds.extend(latlng);

        const fillColor = index === 0
          ? THEME_COLORS.categories.transport.main
          : index === stopsToMark.length - 1
            ? THEME_COLORS.primary.main
            : THEME_COLORS.secondary.main;

        // Custom numbered circle marker using divIcon
        const icon = L.divIcon({
          className: 'road-trip-marker',
          html: `<div style="
            width: 32px; height: 32px; border-radius: 50%;
            background-color: ${fillColor}; color: #fff;
            display: flex; align-items: center; justify-content: center;
            font-weight: 700; font-size: 14px; font-family: sans-serif;
            border: 3px solid #fff;
            box-shadow: 0 2px 6px rgba(0,0,0,0.35);
          ">${index + 1}</div>`,
          iconSize: [32, 32],
          iconAnchor: [16, 16],
          popupAnchor: [0, -18]
        });

        const leg = routeData?.legs?.[index - 1];
        const legInfo = leg ? `${leg.duration} - ${leg.distance}` : '';
        const popupContent = `<div style="font-family: sans-serif; padding: 4px;">
          <strong>${stop.name}</strong>${legInfo ? `<br/><small>${legInfo}</small>` : ''}
        </div>`;

        const marker = L.marker(latlng, { icon, title: stop.name })
          .bindPopup(popupContent)
          .addTo(map);

        markersRef.current.push(marker);
      });

      // Add saved POI markers
      updatePoiMarkers(map, bounds);

      // Fit map to bounds with padding
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [50, 50] });
      }

      // Force Leaflet to recalculate size (fixes grey tiles on first render)
      setTimeout(() => map.invalidateSize(), 100);
    } catch (err) {
      console.error('Map rendering error:', err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeData, showFullRoute, allRoutesData]);

  // Helper: update only the POI markers on the map (without full re-render)
  const updatePoiMarkers = useCallback((map, existingBounds) => {
    if (!map || !routeData) return;

    // Clear previous POI markers
    poiMarkersRef.current.forEach(m => map.removeLayer(m));
    poiMarkersRef.current = [];

    // Collect all saved POIs from the active route day's stops
    const dayStops = days[activeRouteDay]?.stops || [];
    const allSavedPois = [];
    dayStops.forEach(stop => {
      (stop.savedPois || []).forEach(poi => {
        allSavedPois.push(poi);
      });
    });

    if (allSavedPois.length === 0) return;

    const bounds = existingBounds || L.latLngBounds([]);

    allSavedPois.forEach(poi => {
      if (!poi.lat || !poi.lng) return;
      const latlng = [poi.lat, poi.lng];
      bounds.extend(latlng);

      // Small star marker for saved POIs
      const poiIcon = L.divIcon({
        className: 'road-trip-poi-marker',
        html: `<div style="
          width: 24px; height: 24px; border-radius: 50%;
          background-color: ${THEME_COLORS.secondary.main}; color: #fff;
          display: flex; align-items: center; justify-content: center;
          font-size: 13px; font-family: sans-serif;
          border: 2px solid #fff;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        ">★</div>`,
        iconSize: [24, 24],
        iconAnchor: [12, 12],
        popupAnchor: [0, -14]
      });

      const popupContent = `<div style="font-family: sans-serif; padding: 4px;">
        <strong>${poi.name}</strong>
        ${poi.rating ? `<br/><small>⭐ ${poi.rating.toFixed(1)} (${(poi.userRatingsTotal || 0).toLocaleString()})</small>` : ''}
        ${poi.address ? `<br/><small>${poi.address}</small>` : ''}
      </div>`;

      const marker = L.marker(latlng, { icon: poiIcon, title: poi.name })
        .bindPopup(popupContent)
        .addTo(map);

      poiMarkersRef.current.push(marker);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeData, days, activeRouteDay]);

  useEffect(() => {
    if (activeStep === 1 && (routeData || Object.keys(allRoutesData).length > 0)) {
      // Destroy existing map so a fresh one is created for the new day
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markersRef.current = [];
        poiMarkersRef.current = [];
        polylineRef.current = null;
      }
      // Small delay to ensure DOM is ready
      const timer = setTimeout(renderMap, 200);
      return () => clearTimeout(timer);
    }
  }, [activeStep, routeData, renderMap, activeRouteDay, showFullRoute, allRoutesData]);

  // Update POI markers when savedPois change (without full map re-render)
  useEffect(() => {
    if (activeStep === 1 && mapInstanceRef.current && routeData) {
      updatePoiMarkers(mapInstanceRef.current);
    }
  }, [activeStep, days, routeData, updatePoiMarkers]);

  // Destroy map when leaving the View Route step (DOM element gets removed by React)
  useEffect(() => {
    if (activeStep !== 1 && mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
      markersRef.current = [];
      poiMarkersRef.current = [];
      polylineRef.current = null;
    }
  }, [activeStep]);

  // Cleanup Leaflet map on unmount
  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      markersRef.current = [];
      poiMarkersRef.current = [];
      polylineRef.current = null;
    };
  }, []);

  // ============ STEP 3: SAVE ============

  const handleOpenSaveDialog = async () => {
    setSaveDialogOpen(true);
    setSaveSuccess(false);
    setSaveMode('new');
    setExistingTrips([]);

    // Fetch user's trips
    try {
      const response = await api.get('/trips');
      if (response.data) {
        const trips = Array.isArray(response.data) ? response.data : response.data.trips || [];
        setExistingTrips(getActiveCreatorTrips(trips));
      }
    } catch (err) {
      console.error('Failed to fetch trips:', err);
    }
  };

  const handleSaveTrip = async () => {
    if (!hasAnyRoute || saveInFlightRef.current) return;

    saveInFlightRef.current = true;
    setSaveLoading(true);
    setError(null);

    try {
      // Build per-day payload with stops, legs, and saved POIs
      const daysPayload = days.map((day, dayIdx) => {
        const dayRoute = allRoutesData[dayIdx];
        if (!dayRoute) return null;

        // Map orderedStops with their saved POIs
        const stopsWithPois = dayRoute.orderedStops.map((os, orderedIndex) => {
          const matchedStop = findMatchingStop(day.stops, os, orderedIndex);
          return {
            name: os.name,
            lat: os.lat,
            lng: os.lng,
            address: os.address || os.name,
            savedPois: (matchedStop?.savedPois || []).map(p => ({
              name: p.name,
              lat: p.lat,
              lng: p.lng,
              address: p.address || '',
              placeId: p.placeId,
              rating: p.rating || null,
              category: p.category
                ? p.category
                : p.types?.includes('restaurant') ? 'restaurant'
                  : p.types?.includes('lodging') ? 'hotel'
                    : p.types?.includes('gas_station') ? 'gas_station'
                      : 'activity'
            }))
          };
        });

        // Build Google Maps URL with POIs interleaved between stops
        const gmapWaypoints = [];
        stopsWithPois.forEach((stop) => {
          gmapWaypoints.push(`${stop.lat},${stop.lng}`);
          (stop.savedPois || []).forEach(poi => {
            gmapWaypoints.push(`${poi.lat},${poi.lng}`);
          });
        });
        const googleMapsUrlWithPois = `https://www.google.com/maps/dir/${gmapWaypoints.join('/')}`;

        return {
          dayIndex: day.dayIndex != null ? day.dayIndex : dayIdx,
          date: day.date || undefined,
          stops: stopsWithPois,
          legs: dayRoute.legs,
          encodedPolyline: dayRoute.encodedPolyline,
          totalDistance: dayRoute.totalDistance,
          totalDuration: dayRoute.totalDuration,
          googleMapsUrl: googleMapsUrlWithPois
        };
      }).filter(Boolean);

      const payload = { days: daysPayload };

      if (editMode && editTripId) {
        // Edit mode: replace old items in the same trip
        payload.tripId = editTripId;
        if (editGroupId) {
          payload.replaceGroupId = editGroupId;
        }
      } else if (itineraryMode && targetTripId) {
        payload.tripId = targetTripId;
      } else if (saveMode === 'new') {
        if (!newTripTitle || !newTripStartDate || !newTripEndDate) {
          setError(t('roadTrip.fillRequiredFields'));
          setSaveLoading(false);
          return;
        }
        payload.newTrip = {
          title: newTripTitle,
          startDate: newTripStartDate,
          endDate: newTripEndDate,
          description: newTripDescription
        };
      } else {
        if (!selectedTripId) {
          setError(t('roadTrip.selectTrip'));
          setSaveLoading(false);
          return;
        }
        payload.tripId = selectedTripId;
      }

      const response = await api.post('/road-trip/save', payload, authConfig);
      
      if (response.data.success) {
        setSaveSuccess(true);
        setSavedTripId(response.data.tripId);
        setHasUnsavedPlaces(false);
        if ((editMode || itineraryMode) && onSaveComplete) {
          onSaveComplete();
        }
      }
    } catch (err) {
      console.error('Save error:', err);
      setError(err.response?.data?.code === 'GROUP_ROAD_TRIP_READ_ONLY'
        ? t('roadTrip.groupRoadTripReadOnly')
        : err.response?.data?.error || t('roadTrip.saveError'));
    } finally {
      saveInFlightRef.current = false;
      setSaveLoading(false);
    }
  };

  const handleSignInForPlan = () => {
    try {
      const toSave = { days, nextDayId, nextStopId };
      sessionStorage.setItem(PENDING_STATE_KEY, JSON.stringify(toSave));
    } catch (e) {
      // ignore
    }
    setSignInDialogOpen(false);
    const returnPath = location.pathname || '/deals';
    const returnSearch = location.pathname === '/deals' ? '?tab=1' : (location.search || '');
    navigate('/login', { state: { from: { pathname: returnPath, search: returnSearch } } });
  };

  const handleRegisterForPlan = () => {
    try {
      const toSave = { days, nextDayId, nextStopId };
      sessionStorage.setItem(PENDING_STATE_KEY, JSON.stringify(toSave));
    } catch (e) {
      // ignore
    }
    setSignInDialogOpen(false);
    const returnPath = location.pathname || '/deals';
    const returnSearch = location.pathname === '/deals' ? '?tab=1' : (location.search || '');
    navigate('/register', { state: { from: { pathname: returnPath, search: returnSearch } } });
  };

  const canAddAnotherItineraryDay = !datePickMode || (() => {
    const lastDay = days[days.length - 1];
    const nextDate = addDaysToDateKey(lastDay?.date || tripStartKey, 1);
    return isDateKeyWithinTrip(nextDate, tripStartKey, tripEndKey);
  })();

  const plannerActionButtonSx = {
    textTransform: 'none',
    borderRadius: 2,
    minHeight: 40,
    px: 2.5,
    py: 0.75,
    fontSize: '0.875rem',
    fontWeight: 600,
  };

  const plannerPrimaryButtonSx = {
    ...plannerActionButtonSx,
    minHeight: 42,
    px: 3,
    color: '#ffffff !important',
    background: THEME_GRADIENTS.primary,
    '& .MuiButton-startIcon': { color: '#ffffff' },
    '&:hover': {
      color: '#ffffff !important',
      background: `linear-gradient(135deg, ${THEME_COLORS.primary.dark} 0%, ${THEME_COLORS.secondary.dark} 100%)`,
      transform: 'translateY(-1px)',
      boxShadow: `0 4px 16px ${THEME_COLORS.primary.main}66`,
    },
    transition: 'all 0.3s ease',
  };

  // ============ RENDER ============

  return (
    <Box>
      {/* Header — hidden in compact/edit mode */}
      {!compact && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            {t('roadTrip.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('roadTrip.subtitle')}
          </Typography>
        </Box>
      )}

      {/* Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: compact ? 2 : 4 }}>
        {STEPS.map((label, index) => (
          <Step key={label}>
            <StepLabel 
              onClick={() => {
                if (index < activeStep) {
                  setActiveStep(index);
                } else if (index === 1 && hasAnyRoute) {
                  setActiveStep(1);
                }
              }}
              sx={{
                cursor: (index < activeStep || (index === 1 && hasAnyRoute)) ? 'pointer' : 'default',
              }}
            >
              {t(`roadTrip.steps.${index}`)}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Sign-in required dialog (create mode only) */}
      {!editMode && (
        <Dialog open={signInDialogOpen} onClose={() => setSignInDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{t('roadTrip.signInToPlan')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              {t('roadTrip.signInToPlanDescription')}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button onClick={() => setSignInDialogOpen(false)} color="inherit">
              {t('common.cancel')}
            </Button>
            <Button variant="outlined" onClick={handleRegisterForPlan}>
              {t('roadTrip.register')}
            </Button>
            <Button variant="contained" onClick={handleSignInForPlan} sx={{ background: THEME_GRADIENTS.primary }}>
              {t('roadTrip.signIn')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Optimize days dialog */}
      <Dialog open={optimizerDialogOpen} onClose={() => !optimizeLoading && setOptimizerDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('roadTrip.optimizeDays')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('roadTrip.optimizeDaysDescription')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>{t('roadTrip.maxDrivingHours')}: {maxDrivingHours} h</Typography>
          <Slider
            value={maxDrivingHours}
            onChange={(_, v) => setMaxDrivingHours(v)}
            min={4}
            max={14}
            step={1}
            valueLabelDisplay="auto"
            marks={[{ value: 6, label: '6' }, { value: 8, label: '8' }, { value: 10, label: '10' }, { value: 12, label: '12' }]}
            sx={{ mt: 1, mb: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOptimizerDialogOpen(false)} disabled={optimizeLoading}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={handleOptimizeDays} disabled={optimizeLoading} startIcon={optimizeLoading ? <CircularProgress size={18} color="inherit" /> : <TuneIcon />} sx={{ background: THEME_GRADIENTS.primary }}>
            {optimizeLoading ? t('common.loading') : t('roadTrip.optimizeAndSplit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit mode success message */}
      {editMode && saveSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t('roadTrip.savedSuccessfully')}
        </Alert>
      )}

      {/* STEP 1: Plan Stops — Google Maps–style condensed layout */}
      {activeStep === 0 && (
        <Card sx={{ p: compact ? 2 : 2.5, borderRadius: 3, overflow: 'visible' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary' }}>
            {t('roadTrip.addStops')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {datePickMode
              ? t('roadTrip.addStopsItineraryDescription', {
                start: tripStartKey ? formatRoadTripDayLabel(tripStartKey) : '',
                end: tripEndKey ? formatRoadTripDayLabel(tripEndKey) : '',
              })
              : t('roadTrip.addStopsDescription')}
          </Typography>

          {/* Condensed day sections with route path */}
          {days.map((day, dayIdx) => {
            const dayStops = day.stops || [];
            return (
              <Box key={day.id} sx={{ mb: 2, overflow: 'visible' }}>
                {/* Compact day header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                  {datePickMode ? (
                    <TextField
                      type="date"
                      size="small"
                      value={day.date || ''}
                      onChange={(event) => handleDayDateChange(dayIdx, event.target.value)}
                      inputProps={{
                        min: tripStartKey || undefined,
                        max: tripEndKey || undefined,
                      }}
                      InputLabelProps={{ shrink: true }}
                      label={t('roadTrip.tripDate')}
                      sx={{
                        minWidth: 170,
                        '& .MuiOutlinedInput-root': {
                          height: 32,
                          fontSize: '0.82rem',
                        },
                      }}
                    />
                  ) : (
                    <Chip
                      icon={<CalendarIcon sx={{ fontSize: 14 }} />}
                      label={day.label}
                      size="small"
                      sx={{ height: 24, fontSize: '0.75rem', fontWeight: 600 }}
                    />
                  )}
                  {datePickMode && day.label && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {day.label}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {dayStops.filter(s => s.lat).length}/{dayStops.length}
                  </Typography>
                  {days.length > 1 && (
                    <Tooltip title={t('common.remove')}>
                      <IconButton aria-label={t('common.remove')} size="small" onClick={() => handleRemoveDay(dayIdx)} sx={{ ml: 'auto', p: 0.5 }}>
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

                {/* Stops with route path (Google Maps style) */}
                <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'visible' }}>
                  {dayStops.map((stop, index) => (
                    <Box
                      key={stop.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        overflow: 'visible',
                        py: 0.5,
                      }}
                    >
                      {/* Route path: dot or pin + connecting line */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 24, flexShrink: 0 }}>
                        {index === 0 ? (
                          <Box sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            border: '2px solid',
                            borderColor: THEME_COLORS.categories.transport.main,
                            backgroundColor: '#fff',
                          }} />
                        ) : index === dayStops.length - 1 ? (
                          <PlaceIcon sx={{ fontSize: 22, color: THEME_COLORS.primary.main }} />
                        ) : (
                          <Box sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: 'grey.400',
                            border: '1px solid',
                            borderColor: 'grey.500',
                          }} />
                        )}
                        {index < dayStops.length - 1 && (
                          <Box sx={{
                            width: 0,
                            height: 14,
                            borderLeft: '2px dashed',
                            borderColor: 'divider',
                            mt: 0.25,
                          }} />
                        )}
                      </Box>

                      {/* Input */}
                      <Box sx={{ flex: 1, overflow: 'visible', minWidth: 0 }}>
                        <LocationAutocomplete
                          compact
                          value={stop.address}
                          onChange={(val) => handleStopChange(dayIdx, stop.id, val)}
                          onPlaceSelect={(placeData) => handlePlaceSelect(dayIdx, stop.id, placeData)}
                          placeholder={index === 0 ? t('roadTrip.startLocation') : index === dayStops.length - 1 ? t('roadTrip.endLocation') : t('roadTrip.searchPlaceholder')}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, backgroundColor: 'grey.50', '& fieldset': { borderColor: 'divider' } } }}
                        />
                      </Box>

                      {/* Compact actions */}
                      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        <Tooltip title={t('roadTrip.moveUp')}>
                          <span>
                            <IconButton size="small" onClick={() => handleMoveStop(dayIdx, index, -1)} disabled={index === 0} sx={{ p: 0.5 }}>
                              <ArrowUpIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={t('roadTrip.moveDown')}>
                          <span>
                            <IconButton size="small" onClick={() => handleMoveStop(dayIdx, index, 1)} disabled={index === dayStops.length - 1} sx={{ p: 0.5 }}>
                              <ArrowDownIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={t('common.remove')}>
                          <span>
                            <IconButton aria-label={t('common.remove')} size="small" onClick={() => handleRemoveStop(dayIdx, stop.id)} disabled={dayStops.length <= 2} sx={{ p: 0.5, color: 'error.main' }}>
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </Box>
                  ))}

                  {/* Add stop and Round trip */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 3.5, mt: 0.25, flexWrap: 'wrap' }}>
                    <Button
                      startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                      onClick={() => handleAddStop(dayIdx)}
                      variant="outlined"
                      size="small"
                      sx={plannerActionButtonSx}
                    >
                      {t('roadTrip.addStop')}
                    </Button>
                    {dayStops.length >= 2 && (dayStops[0]?.name || dayStops[0]?.address) && (
                      <Button
                        startIcon={<RouteIcon sx={{ fontSize: 18 }} />}
                        onClick={() => handleRoundTrip(dayIdx)}
                        variant="outlined"
                        size="small"
                        sx={plannerActionButtonSx}
                      >
                        {t('roadTrip.roundTrip')}
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
            <Button
              onClick={handleAddDay}
              startIcon={<AddIcon />}
              variant="outlined"
              size="small"
              disabled={!canAddAnotherItineraryDay}
              sx={{ ...plannerActionButtonSx, borderStyle: 'dashed' }}
            >
              {t('roadTrip.addDay')}
            </Button>
          </Box>

          {/* Plan route button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              startIcon={routeLoading ? <CircularProgress size={20} color="inherit" /> : <CarIcon />}
              onClick={handlePlanRoute}
              disabled={!canPlanRoute || routeLoading}
              sx={plannerPrimaryButtonSx}
            >
              {routeLoading ? t('roadTrip.planning') : t('roadTrip.planMyRoute')}
            </Button>
          </Box>
        </Card>
      )}

      {/* STEP 2: View Route */}
      {activeStep === 1 && routeData && (
        <Box>
          {/* Day tabs — only show days that have routes (empty days are skipped) */}
          {Object.keys(allRoutesData).length > 1 && (
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
              {days.map((day, idx) => {
                const dr = allRoutesData[idx];
                if (!dr) return null; // Skip days without routes
                const isActive = activeRouteDay === idx;
                return (
                  <Button
                    key={day.id}
                    variant={isActive ? 'contained' : 'outlined'}
                    onClick={() => setActiveRouteDay(idx)}
                    startIcon={<CalendarIcon />}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 2.5,
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      px: 3,
                      py: 1.2,
                      ...(isActive ? {
                        background: THEME_GRADIENTS.primary,
                        color: '#fff',
                        boxShadow: `0 4px 14px ${THEME_COLORS.primary.main}44`,
                        '&:hover': { background: `linear-gradient(135deg, ${THEME_COLORS.primary.dark} 0%, ${THEME_COLORS.secondary.dark} 100%)` }
                      } : {
                        borderColor: THEME_COLORS.primary.main,
                        color: THEME_COLORS.primary.main,
                        '&:hover': { backgroundColor: `${THEME_COLORS.primary.main}0A` }
                      })
                    }}
                  >
                    {day.label}
                    {dr && (
                      <Chip
                        label={`${dr.stopCount} ${t('roadTrip.stops')}`}
                        size="small"
                        sx={{
                          ml: 1,
                          height: 22,
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          backgroundColor: isActive ? 'rgba(255,255,255,0.25)' : `${THEME_COLORS.primary.main}15`,
                          color: isActive ? '#fff' : THEME_COLORS.primary.main,
                        }}
                      />
                    )}
                  </Button>
                );
              })}
            </Box>
          )}

          <Box sx={{ containerType: 'inline-size' }}>
          <Box sx={routeStepGridSx}>
          <Box sx={{ gridArea: 'top', minWidth: 0 }}>
          {/* Total Route breadcrumb — full end-to-end path across all days */}
          {(() => {
            const fullStops = getFlattenedStopsForOptimizer();
            const routePath = fullStops.map(s => s.name || s.address || '').filter(Boolean).join(' → ');
            return (
              <Box sx={{
                mb: 2,
                px: 2.5,
                py: 1.5,
                borderRadius: 2.5,
                background: THEME_GRADIENTS.primary,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 0.5
              }}>
                <RouteIcon sx={{ fontSize: 20, opacity: 0.9, flexShrink: 0, mr: 0.5 }} />
                <Typography variant="body2" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }}>
                  {t('roadTrip.totalRoute')}: {routePath || routeData?.orderedStops?.map(s => s.name).join(' → ') || '—'}
                </Typography>
              </Box>
            );
          })()}

          {/* Map with breadcrumb toggle — full route = end-to-end path across all days */}
          <Card sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', p: 1, backgroundColor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
              {Object.keys(allRoutesData).length > 1 ? (
                <Tooltip title={showFullRoute ? t('roadTrip.showDayRoute') : t('roadTrip.showFullRoute')}>
                  <Chip
                    icon={<RouteIcon sx={{ fontSize: 18 }} />}
                    label={showFullRoute ? t('roadTrip.showDayRoute') : t('roadTrip.showFullRoute')}
                    onClick={() => setShowFullRoute(v => !v)}
                    size="small"
                    sx={{ cursor: 'pointer' }}
                  />
                </Tooltip>
              ) : (
                <Chip icon={<RouteIcon sx={{ fontSize: 18 }} />} label={t('roadTrip.showFullRoute')} size="small" variant="outlined" />
              )}
            </Box>
            <Box 
              ref={mapRef} 
              sx={{ 
                width: '100%', 
                height: { xs: 300, md: 450 },
                backgroundColor: THEME_COLORS.background.light
              }} 
            />
          </Card>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<NavigationIcon />}
              href={(() => {
                // Build Google Maps URL with POIs interleaved between main stops
                const waypoints = [];
                const currentDayStops = days[activeRouteDay]?.stops || [];
                routeData.orderedStops.forEach((os, orderedIndex) => {
                  waypoints.push(`${os.lat},${os.lng}`);
                  const matched = findMatchingStop(currentDayStops, os, orderedIndex);
                  (matched?.savedPois || []).forEach(poi => {
                    waypoints.push(`${poi.lat},${poi.lng}`);
                  });
                });
                return waypoints.length > 0
                  ? `https://www.google.com/maps/dir/${waypoints.join('/')}`
                  : routeData.googleMapsUrl;
              })()}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 600,
                background: THEME_GRADIENTS.success,
                '&:hover': {
                  background: `linear-gradient(90deg, ${THEME_COLORS.categories.transport.dark} 0%, ${THEME_COLORS.categories.transport.main} 100%)`,
                }
              }}
            >
              {t('roadTrip.openInGoogleMaps')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => setActiveStep(0)}
              sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}
            >
              {t('roadTrip.editStops')}
            </Button>
            {getFlattenedStopsForOptimizer().length >= 2 && (
              <Button
                variant="outlined"
                startIcon={<TuneIcon />}
                onClick={() => setOptimizerDialogOpen(true)}
                sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}
              >
                {t('roadTrip.optimizeDays')}
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={saveLoading ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              onClick={editMode || itineraryMode ? handleSaveTrip : handleOpenSaveDialog}
              disabled={saveLoading}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 600,
                ml: 'auto',
                background: THEME_GRADIENTS.primary,
                '&:hover': {
                  background: `linear-gradient(135deg, ${THEME_COLORS.primary.dark} 0%, ${THEME_COLORS.secondary.dark} 100%)`,
                }
              }}
            >
              {saveButtonLabel}
            </Button>
          </Box>

          </Box>

          <Box sx={{ gridArea: 'panel', minWidth: 0, ...getRouteStepPanelSx(editMode || itineraryMode) }}>
            <RoadTripStopsPanel
              dayLabel={Object.keys(allRoutesData).length > 1 ? days[activeRouteDay]?.label : null}
              stops={getStopsWithPlaces(days[activeRouteDay]?.stops)}
              onRemovePlace={(stopId, placeId) => handleRemovePlace(activeRouteDay, stopId, placeId)}
              onEditStops={() => setActiveStep(0)}
              onSave={editMode || itineraryMode ? handleSaveTrip : handleOpenSaveDialog}
              saving={saveLoading}
              saveLabel={saveButtonLabel}
              hasUnsavedChanges={hasUnsavedPlaces}
              t={t}
            />
          </Box>

          <Box sx={{ gridArea: 'details', minWidth: 0 }}>
          {/* Per-leg breakdown with places to visit */}
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            {t('roadTrip.routeDetails')}
          </Typography>
          
          {routeData.legs.map((leg, index) => {
            // Get POI data for the start city of this leg
            const startStopPois = poisData ? poisData[index] : null;

            return (
              <Accordion 
                key={index} 
                defaultExpanded={index === 0}
                sx={{ 
                  mb: 1, 
                  borderRadius: '12px !important', 
                  '&:before': { display: 'none' },
                  boxShadow: THEME_SHADOWS.light
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <CarIcon color="primary" />
                    <Typography sx={{ fontWeight: 600, flex: 1 }}>
                      {routeData.orderedStops[index]?.name} {t('roadTrip.to')} {routeData.orderedStops[index + 1]?.name}
                    </Typography>
                    <Chip 
                      label={`${leg.duration} · ${leg.distance}`}
                      size="small"
                      sx={{ fontWeight: 600, backgroundColor: `${THEME_COLORS.primary.main}1A`, color: THEME_COLORS.primary.main }}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {poisStatusByDay[activeRouteDay] === 'loading' ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={28} />
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                        {t('roadTrip.findingPlaces')}
                      </Typography>
                    </Box>
                  ) : startStopPois ? (
                    <Box>
                      {/* Start city places */}
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: THEME_COLORS.primary.main }}>
                        {startStopPois.stopName
                          ? t('roadTrip.placesAlongWayTo', { place: startStopPois.stopName })
                          : t('roadTrip.placesToVisit')}
                      </Typography>

                      <POISection 
                        title={t('roadTrip.restaurants')}
                        icon={<RestaurantIcon sx={{ color: THEME_COLORS.categories.restaurant.main }} />}
                        pois={startStopPois.restaurants}
                        onToggle={(poi) => handleAddPOIToRoute(poi, index, activeRouteDay)}
                        savedPlaceIds={getSavedPlaceIds(index, activeRouteDay)}
                        t={t}
                        category="restaurant"
                      />
                      <POISection 
                        title={t('roadTrip.hotels')}
                        icon={<HotelIcon sx={{ color: THEME_COLORS.categories.hotel.main }} />}
                        pois={startStopPois.hotels}
                        onToggle={(poi) => handleAddPOIToRoute(poi, index, activeRouteDay)}
                        savedPlaceIds={getSavedPlaceIds(index, activeRouteDay)}
                        t={t}
                        category="hotel"
                      />
                      <POISection 
                        title={t('roadTrip.attractions')}
                        icon={<AttractionsIcon sx={{ color: THEME_COLORS.primary.main }} />}
                        pois={startStopPois.attractions}
                        onToggle={(poi) => handleAddPOIToRoute(poi, index, activeRouteDay)}
                        savedPlaceIds={getSavedPlaceIds(index, activeRouteDay)}
                        t={t}
                        category="attraction"
                      />
                      <POISection
                        title={t('roadTrip.gasStations')}
                        icon={<GasStationIcon sx={{ color: THEME_COLORS.secondary.main }} />}
                        pois={startStopPois.gasStations}
                        onToggle={(poi) => handleAddPOIToRoute(poi, index, activeRouteDay)}
                        savedPlaceIds={getSavedPlaceIds(index, activeRouteDay)}
                        t={t}
                        category="gas_station"
                      />

                      {!startStopPois.restaurants?.length && !startStopPois.hotels?.length && !startStopPois.attractions?.length
                        && !startStopPois.gasStations?.length
                        && renderNoPois(startStopPois.unavailable)}
                    </Box>
                  ) : renderNoPois(poisStatusByDay[activeRouteDay] === 'failed')}
                </AccordionDetails>
              </Accordion>
            );
          })}

          {/* Last stop (destination) places -- not covered by legs */}
          {poisData && poisData.length > routeData.legs.length && (() => {
            const lastStopPois = poisData[poisData.length - 1];
            return (
              <Accordion
                defaultExpanded={false}
                sx={{
                  mb: 1,
                  borderRadius: '12px !important',
                  '&:before': { display: 'none' },
                  boxShadow: THEME_SHADOWS.light
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <PlaceIcon sx={{ color: THEME_COLORS.primary.main }} />
                    <Typography sx={{ fontWeight: 600, flex: 1 }}>
                      {lastStopPois.stopName} — {t('roadTrip.placesToVisit')}
                    </Typography>
                    <Chip
                      label={`${(lastStopPois.restaurants?.length || 0) + (lastStopPois.hotels?.length || 0) + (lastStopPois.attractions?.length || 0) + (lastStopPois.gasStations?.length || 0)} places`}
                      size="small"
                      sx={{ fontWeight: 600, backgroundColor: `${THEME_COLORS.secondary.main}1A`, color: THEME_COLORS.secondary.main }}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <POISection
                      title={t('roadTrip.restaurants')}
                      icon={<RestaurantIcon sx={{ color: THEME_COLORS.categories.restaurant.main }} />}
                      pois={lastStopPois.restaurants}
                      onToggle={(poi) => handleAddPOIToRoute(poi, routeData.orderedStops.length - 1, activeRouteDay)}
                      savedPlaceIds={getSavedPlaceIds(routeData.orderedStops.length - 1, activeRouteDay)}
                      t={t}
                      category="restaurant"
                    />
                    <POISection
                      title={t('roadTrip.hotels')}
                      icon={<HotelIcon sx={{ color: THEME_COLORS.categories.hotel.main }} />}
                      pois={lastStopPois.hotels}
                      onToggle={(poi) => handleAddPOIToRoute(poi, routeData.orderedStops.length - 1, activeRouteDay)}
                      savedPlaceIds={getSavedPlaceIds(routeData.orderedStops.length - 1, activeRouteDay)}
                      t={t}
                      category="hotel"
                    />
                    <POISection
                      title={t('roadTrip.attractions')}
                      icon={<AttractionsIcon sx={{ color: THEME_COLORS.primary.main }} />}
                      pois={lastStopPois.attractions}
                      onToggle={(poi) => handleAddPOIToRoute(poi, routeData.orderedStops.length - 1, activeRouteDay)}
                      savedPlaceIds={getSavedPlaceIds(routeData.orderedStops.length - 1, activeRouteDay)}
                      t={t}
                      category="attraction"
                    />
                    <POISection
                      title={t('roadTrip.gasStations')}
                      icon={<GasStationIcon sx={{ color: THEME_COLORS.secondary.main }} />}
                      pois={lastStopPois.gasStations}
                      onToggle={(poi) => handleAddPOIToRoute(poi, routeData.orderedStops.length - 1, activeRouteDay)}
                      savedPlaceIds={getSavedPlaceIds(routeData.orderedStops.length - 1, activeRouteDay)}
                      t={t}
                      category="gas_station"
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })()}
          </Box>
          </Box>
          </Box>
        </Box>
      )}

      {/* SAVE DIALOG */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {saveSuccess ? t('roadTrip.tripSaved') : t('roadTrip.saveRoadTrip')}
        </DialogTitle>
        <DialogContent>
          {saveSuccess ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="h6" color="success.main" sx={{ mb: 2 }}>
                {t('roadTrip.savedSuccessfully')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('roadTrip.savedDescription')}
              </Typography>
              <Button
                variant="contained"
                href={`/trips/${savedTripId}`}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  fontWeight: 600,
                  background: THEME_GRADIENTS.primary,
                }}
              >
                {t('roadTrip.viewTrip')}
              </Button>
            </Box>
          ) : (
            <Box sx={{ mt: 1 }}>
              {/* Save mode toggle */}
              <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                <Chip
                  label={t('roadTrip.createNewTrip')}
                  onClick={() => setSaveMode('new')}
                  color={saveMode === 'new' ? 'primary' : 'default'}
                  variant={saveMode === 'new' ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 600, cursor: 'pointer' }}
                />
                {existingTrips.length > 0 && (
                  <Chip
                    label={t('roadTrip.addToExistingTrip')}
                    onClick={() => setSaveMode('existing')}
                    color={saveMode === 'existing' ? 'primary' : 'default'}
                    variant={saveMode === 'existing' ? 'filled' : 'outlined'}
                    sx={{ fontWeight: 600, cursor: 'pointer' }}
                  />
                )}
              </Box>

              {saveMode === 'new' || existingTrips.length === 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label={t('roadTrip.tripTitle')}
                    value={newTripTitle}
                    onChange={(e) => setNewTripTitle(e.target.value)}
                    fullWidth
                    required
                  />
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label={t('roadTrip.startDate')}
                      type="date"
                      value={newTripStartDate}
                      onChange={(e) => setNewTripStartDate(e.target.value)}
                      fullWidth
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label={t('roadTrip.endDate')}
                      type="date"
                      value={newTripEndDate}
                      onChange={(e) => setNewTripEndDate(e.target.value)}
                      fullWidth
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                  <TextField
                    label={t('roadTrip.description')}
                    value={newTripDescription}
                    onChange={(e) => setNewTripDescription(e.target.value)}
                    fullWidth
                    multiline
                    rows={3}
                  />
                </Box>
              ) : (
                <FormControl fullWidth>
                  <InputLabel>{t('roadTrip.selectExistingTrip')}</InputLabel>
                  <Select
                    value={selectedTripId}
                    onChange={(e) => setSelectedTripId(e.target.value)}
                    label={t('roadTrip.selectExistingTrip')}
                  >
                    {existingTrips.map(trip => (
                      <MenuItem key={trip.id} value={trip.id}>
                        {trip.title}
                        {parseTripDate(trip.start_date) && (
                          <> · {format(parseTripDate(trip.start_date), 'MMM d, yyyy')}</>
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        {!saveSuccess && (
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setSaveDialogOpen(false)} sx={{ textTransform: 'none' }}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveTrip}
              disabled={saveLoading}
              startIcon={saveLoading ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 600,
                background: THEME_GRADIENTS.primary,
              }}
            >
              {saveLoading ? t('common.loading') : t('roadTrip.saveTrip')}
            </Button>
          </DialogActions>
        )}
      </Dialog>

    </Box>
  );
};

// ============ POI Card category gradients (used when no photo available) ============

const CATEGORY_GRADIENTS = {
  restaurant: `linear-gradient(135deg, ${THEME_COLORS.categories.restaurant.main}CC 0%, ${THEME_COLORS.categories.restaurant.main}99 100%)`,
  hotel: `linear-gradient(135deg, ${THEME_COLORS.categories.hotel.main}CC 0%, ${THEME_COLORS.categories.hotel.main}99 100%)`,
  attraction: `linear-gradient(135deg, ${THEME_COLORS.primary.main}CC 0%, ${THEME_COLORS.secondary.main}99 100%)`,
  gas_station: `linear-gradient(135deg, ${THEME_COLORS.secondary.main}CC 0%, ${THEME_COLORS.secondary.main}99 100%)`,
};

const CATEGORY_ICONS = {
  restaurant: <RestaurantIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.85)' }} />,
  hotel: <HotelIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.85)' }} />,
  attraction: <AttractionsIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.85)' }} />,
  gas_station: <GasStationIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.85)' }} />,
};

// ============ POI Section Sub-component ============

const POISection = ({ title, icon, pois, onToggle, savedPlaceIds, t, category }) => {
  if (!pois || pois.length === 0) return null;

  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        {icon}
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
        <Chip label={pois.length} size="small" variant="outlined" />
      </Box>
      <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1.5, '&::-webkit-scrollbar': { height: 6 }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#ccc', borderRadius: 3 } }}>
        {pois.map((poi) => {
          const isSelected = savedPlaceIds?.has(poi.placeId);

          return (
            <Card 
              key={poi.placeId} 
              onClick={() => onToggle({ ...poi, category: category || poi.category })}
              sx={{ 
                minWidth: 240, 
                maxWidth: 260, 
                flexShrink: 0, 
                borderRadius: 2.5, 
                boxShadow: isSelected ? `0 0 0 2px ${THEME_COLORS.primary.main}` : THEME_SHADOWS.light,
                transition: 'all 0.2s ease',
                overflow: 'hidden',
                cursor: 'pointer',
                position: 'relative',
                opacity: isSelected ? 1 : 0.92,
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: isSelected ? `0 0 0 2px ${THEME_COLORS.primary.main}, ${THEME_SHADOWS.medium}` : THEME_SHADOWS.medium,
                  opacity: 1
                }
              }}
            >
              {/* Selected checkmark badge */}
              {isSelected && (
                <Box sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 2,
                  backgroundColor: '#fff',
                  borderRadius: '50%',
                  width: 26,
                  height: 26,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                }}>
                  <CheckCircleIcon sx={{ fontSize: 22, color: THEME_COLORS.primary.main }} />
                </Box>
              )}

              {/* Photo or category gradient placeholder */}
              {poi.photoUrl ? (
                <CardMedia
                  component="img"
                  height="140"
                  image={poi.photoUrl}
                  alt={poi.name}
                  sx={{ objectFit: 'cover' }}
                />
              ) : (
                <Box
                  sx={{
                    height: 100,
                    background: CATEGORY_GRADIENTS[category] || CATEGORY_GRADIENTS.attraction,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {CATEGORY_ICONS[category] || CATEGORY_ICONS.attraction}
                </Box>
              )}

              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, lineHeight: 1.3 }} noWrap>
                  {poi.name}
                </Typography>

                {/* Rating row */}
                {poi.rating ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: THEME_COLORS.primary.main }}>
                      {poi.rating.toFixed(1)}
                    </Typography>
                    <Rating value={poi.rating} precision={0.1} size="small" readOnly sx={{ fontSize: '0.9rem' }} />
                    {poi.userRatingsTotal > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        ({poi.userRatingsTotal.toLocaleString()})
                      </Typography>
                    )}
                  </Box>
                ) : null}

                {/* Price level + open/closed */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                  {poi.priceLevel !== null && poi.priceLevel !== undefined && poi.priceLevel > 0 && (
                    <Chip
                      label={'$'.repeat(poi.priceLevel)}
                      size="small"
                      sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#f0f0f0' }}
                    />
                  )}
                  {poi.openNow !== null && (
                    <Chip
                      label={poi.openNow ? t('roadTrip.open') : t('roadTrip.closed')}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        backgroundColor: poi.openNow ? '#e8f5e9' : '#fce4ec',
                        color: poi.openNow ? '#2e7d32' : '#c62828'
                      }}
                    />
                  )}
                </Box>

                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {poi.address}
                </Typography>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default RoadTripPlanner;
